import { useMemo } from "react";
import { TaskType } from "@common/types/task";
import { FinancialDocumentViewSchema } from "@common/types/views/financialDocumentViews";
import { financialDocumentsViewConfig } from "@components/documents/FinancialDocumentsView/financialDocumentsViewConfig";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { getFinancialDocumentsTaskFilterTabs } from "@components/tasks/TaskBody/FinancialDocuments/financialDocumentsTaskFilters";
import {
    defaultFinancialDocumentsView,
    parseFinancialDocumentsTaskView,
    serializeFinancialDocumentsTemplateView,
} from "@components/tasks/TaskBody/FinancialDocuments/financialDocumentsTaskView";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { useScope } from "@state/scopeContext.lib";
import styles from "./CloseTemplateTaskTransactionsPreview.module.scss";
import { TemplateFinancialDocumentsDataTable } from "./dataPreview/TemplateFinancialDocumentsDataTable";
import { TemplatePreviewSwitcher } from "./dataPreview/TemplatePreviewSwitcher";
import { useTemplateTaskPreview } from "./dataPreview/useTemplateTaskPreview";
import { useCloseTemplateTaskViewState } from "./useCloseTemplateTaskViewState";

interface Props {
    viewConfig: Record<string, unknown> | null;
    onViewConfigChange: (viewConfig: Record<string, unknown>) => void;
}

export const CloseTemplateTaskFinancialDocumentsPreview: React.FC<Props> = ({
    viewConfig,
    onViewConfigChange,
}) => {
    const { workspaceId } = useScope();
    const { view, handleViewChange, handleFiltersChange } =
        useCloseTemplateTaskViewState({
            viewConfig,
            onViewConfigChange,
            parseViewConfig: parseFinancialDocumentsTaskView,
            serializeView: serializeFinancialDocumentsTemplateView,
            viewSchema: FinancialDocumentViewSchema,
        });

    const defaultView = useMemo(() => defaultFinancialDocumentsView(), []);
    const availableFilters = useMemo(
        () =>
            getFinancialDocumentsTaskFilterTabs({
                isWorkspaceScoped: !!workspaceId,
            }),
        [workspaceId],
    );

    const { previewTarget, resolvedView } = useTemplateTaskPreview({
        taskType: TaskType.FINANCIAL_DOCUMENTS,
        viewConfig: viewConfig ?? serializeFinancialDocumentsTemplateView(view),
    });

    return (
        <div>
            <TablePageHeader
                className={styles.filters}
                search={view.filters.search}
                onSearchChange={(search) => handleFiltersChange({ search })}
                filters={view.filters}
                onFiltersChange={handleFiltersChange}
                availableFilters={availableFilters}
                isFullPage
            >
                <ColumnSettings
                    view={view}
                    viewConfig={financialDocumentsViewConfig}
                    defaultView={defaultView}
                    onViewChange={handleViewChange}
                />
                <TemplatePreviewSwitcher target={previewTarget} />
            </TablePageHeader>

            <TemplateFinancialDocumentsDataTable
                templateView={view}
                onTemplateViewChange={handleViewChange}
                resolvedView={resolvedView}
                workspaceId={previewTarget.workspaceId}
            />
        </div>
    );
};
